import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import type { ImportacionDeComercia } from '../types/comercia.types';
import { cantidadDe } from '@/lib/utils/formato-precio';

export type PasoDeLogistica = 'origen-destino' | 'carga' | 'rutas';

const PASOS: Array<{ clave: PasoDeLogistica; titulo: string; href: string }> = [
  {
    clave: 'origen-destino',
    titulo: 'Origen y destino',
    href: '/comercia/logistica/origen-destino',
  },
  { clave: 'carga', titulo: 'Carga', href: '/comercia/logistica/carga' },
  { clave: 'rutas', titulo: 'Rutas', href: '/comercia/logistica/rutas' },
];

/** Cómo va cada paso, leído de la importación: el resumen que va debajo del nombre. */
export function estadoDeLosPasos(
  imp: ImportacionDeComercia | null
): Record<PasoDeLogistica, { listo: boolean; detalle: string }> {
  const carga = imp ? resumenDeCarga(imp) : null;
  const ruta = imp?.ruta && !imp.ruta.desactualizada ? imp.ruta : null;
  return {
    'origen-destino': {
      listo: !!imp?.origen && !!imp.destino,
      detalle:
        imp?.origen && imp.destino ? `${imp.origen.ciudad} → ${imp.destino.ciudad}` : 'pendiente',
    },
    carga: { listo: !!carga, detalle: carga ?? 'pendiente' },
    rutas: {
      listo: !!ruta,
      detalle: ruta
        ? `vía ${ruta.via ?? 'directa'} · ${ruta.transportistas.join(' + ')}`
        : imp?.ruta?.desactualizada
          ? 'elige de nuevo'
          : 'pendiente',
    },
  };
}

/** «1 × 20' estándar», «2 × 40' High Cube + 1 × 20' estándar». */
export function resumenDeCarga(imp: ImportacionDeComercia): string | null {
  if (imp.contenedores.length === 0) return null;
  const porTipo = new Map<string, { nombre: string; n: number }>();
  for (const c of imp.contenedores) {
    const p = porTipo.get(c.tipo.id);
    if (p) p.n += 1;
    else porTipo.set(c.tipo.id, { nombre: c.tipo.nombre, n: 1 });
  }
  return [...porTipo.values()].map((t) => `${t.n} × ${t.nombre}`).join(' + ');
}

/**
 * Los tres pasos de Logística en una franja (maqueta): número o tilde, el nombre,
 * su resumen debajo y una línea que los une. A la derecha, el botón para seguir.
 */
export function PasosDeLogistica({
  actual,
  importacion,
  siguiente,
}: {
  actual: PasoDeLogistica;
  importacion: ImportacionDeComercia | null;
  /** El botón de la derecha: «Continuar», «Ver costo nacionalizado». */
  siguiente: { texto: string; href: string; deshabilitado?: boolean; aviso?: string } | null;
}) {
  const estados = estadoDeLosPasos(importacion);
  return (
    <nav
      aria-label="Pasos de logística"
      className="flex flex-col gap-4 rounded-tarjeta border bg-card px-5 py-4 shadow-sm lg:flex-row lg:items-center lg:gap-6"
    >
      <ol className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-0">
        {PASOS.map((paso, i) => {
          const estado = estados[paso.clave];
          const esActual = paso.clave === actual;
          const listo = estado.listo && !esActual;
          return (
            <Fragment key={paso.clave}>
              <li className={cn('flex min-w-0 items-center', i > 0 && 'sm:flex-1')}>
                {i > 0 && (
                  <span aria-hidden="true" className="mx-4 hidden h-px flex-1 bg-border sm:block" />
                )}
                <Link
                  to={paso.href}
                  aria-current={esActual ? 'step' : undefined}
                  className="group flex shrink-0 items-center gap-3 rounded-lg p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Circulo numero={i + 1} listo={listo} actual={esActual} />
                  <span className="min-w-0">
                    <span
                      className={cn(
                        'block text-[15px] font-semibold leading-tight',
                        !esActual && !listo && 'text-muted-foreground'
                      )}
                    >
                      {paso.titulo}
                    </span>
                    <span className="block max-w-[260px] truncate text-xs text-muted-foreground">
                      {estado.detalle}
                    </span>
                  </span>
                </Link>
              </li>
            </Fragment>
          );
        })}
      </ol>
      {siguiente && (
        <div className="flex shrink-0 flex-col items-stretch gap-1 lg:items-end">
          {siguiente.deshabilitado ? (
            <span
              aria-disabled="true"
              className={buttonVariants({
                variant: 'degradado',
                size: 'lg',
                className: 'pointer-events-none px-6 opacity-50',
              })}
            >
              {siguiente.texto}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          ) : (
            <Link
              to={siguiente.href}
              className={buttonVariants({ variant: 'degradado', size: 'lg', className: 'px-6' })}
            >
              {siguiente.texto}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          )}
          {siguiente.aviso && (
            <span className="text-xs text-muted-foreground">{siguiente.aviso}</span>
          )}
        </div>
      )}
    </nav>
  );
}

function Circulo({
  numero,
  listo,
  actual,
}: {
  numero: number;
  listo: boolean;
  actual: boolean;
}): ReactNode {
  if (listo)
    return (
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ok-suave text-ok">
        <Check className="h-4 w-4" aria-label="listo" />
      </span>
    );
  return (
    <span
      className={cn(
        'grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-semibold tabular-nums',
        actual ? 'bg-degradado-prixia text-white shadow-md' : 'bg-muted text-muted-foreground'
      )}
    >
      {numero}
    </span>
  );
}

/** «3 productos». */
export const productosDe = (n: number) => cantidadDe(n, 'producto', 'productos');
